import { useEffect, useRef } from 'react';
import { saveBarCache, loadBarCacheEntry, barLimit } from './useBarCache';
import { useTerminalStore } from '../store/useTerminalStore';
import { gatewayApi } from '../lib/api';
import { fetchBarsWithFallback } from '../lib/bars';

function mapAndDedup(gatewayBars: Awaited<ReturnType<typeof gatewayApi.bars>>) {
  const mapped = gatewayBars.map((b) => ({
    time: new Date(b.bar_close_time).getTime() / 1000,
    open: b.open,
    high: b.high,
    low: b.low,
    close: b.close,
    volume: b.volume,
  }));
  const dedup = new Map<number, typeof mapped[0]>();
  for (const bar of mapped) dedup.set(bar.time, bar);
  return [...dedup.values()].sort((a, b) => a.time - b.time);
}

async function refreshVarCone(symbol: string): Promise<void> {
  useTerminalStore.getState().setPanelStatus('varcone', 'loading');
  useTerminalStore.setState({ varCone: null });
  try {
    const varCone = await gatewayApi.varcone(symbol);
    if (useTerminalStore.getState().selectedSymbol !== symbol) return;
    useTerminalStore.setState({ varCone });
    useTerminalStore.getState().setPanelStatus('varcone', 'ready');
  } catch {
    if (useTerminalStore.getState().selectedSymbol !== symbol) return;
    useTerminalStore.setState({ varCone: null });
    useTerminalStore.getState().setPanelStatus('varcone', 'degraded');
  }
}

export function useBarPolling(intervalMs: number = 30_000, enabled: boolean = true): void {
  const lastTfRef = useRef<string>('');
  const lastSymRef = useRef<string>('');
  const lastFbRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!enabled) return;
    async function poll() {
      if (document.hidden) return;

      try {
        const sym = useTerminalStore.getState().selectedSymbol;
        const tf = useTerminalStore.getState().selectedTimeframe;

        const barsResult = await fetchBarsWithFallback(sym, tf, barLimit(tf));
        useTerminalStore.getState().setBarsSource(barsResult.timeframeUsed, barsResult.isFallback);
        const sortedBars = mapAndDedup(barsResult.bars);

        const tfChanged = lastTfRef.current !== '' && lastTfRef.current !== tf;
        const symChanged = lastSymRef.current !== '' && lastSymRef.current !== sym;
        // When a symbol is onboarded mid-session its default-timeframe source flips
        // from 1m-proxy (isFallback=true) to native dollar bars (isFallback=false).
        // Merging dollar bars into the existing 1m series would yield a mixed chart,
        // so a fallback-state transition forces a full replace (same as a tf switch).
        const fbChanged = lastFbRef.current !== null && lastFbRef.current !== barsResult.isFallback;
        lastTfRef.current = tf;
        lastSymRef.current = sym;
        lastFbRef.current = barsResult.isFallback;

        if (tfChanged || symChanged || fbChanged) {
          useTerminalStore.getState().setBars(sortedBars);
          saveBarCache(sym, tf, sortedBars, barsResult.isFallback);
        } else {
          const existing = useTerminalStore.getState().bars;
          const merged = new Map<number, typeof sortedBars[0]>();
          for (const bar of existing) merged.set(bar.time, bar);
          for (const bar of sortedBars) merged.set(bar.time, bar);
          const final = [...merged.values()].sort((a, b) => a.time - b.time);
          const limit = barLimit(tf);
          const capped = final.length > limit ? final.slice(-limit) : final;
          useTerminalStore.getState().setBars(capped);
          saveBarCache(sym, tf, capped, barsResult.isFallback);
        }
      } catch {
        // Swallow errors — polling is best-effort
      }
    }

    let prevSym = useTerminalStore.getState().selectedSymbol;
    let prevTf = useTerminalStore.getState().selectedTimeframe;

    const unsub = useTerminalStore.subscribe(async (state) => {
      const symbolChanged = state.selectedSymbol !== prevSym;
      const timeframeChanged = state.selectedTimeframe !== prevTf;
      if (symbolChanged || timeframeChanged) {
        prevSym = state.selectedSymbol;
        prevTf = state.selectedTimeframe;
        const sym = state.selectedSymbol;
        const tf = state.selectedTimeframe;

        if (symbolChanged) {
          void refreshVarCone(sym);
        }

        const cachedEntry = loadBarCacheEntry(sym, tf);
        useTerminalStore.getState().setBars(cachedEntry && cachedEntry.bars.length > 0 ? cachedEntry.bars : []);
        // Restore the fallback badge from cache synchronously so the previous
        // selection's 1m-proxy marker does not linger over the new chart.
        useTerminalStore.getState().setBarsSource(tf, cachedEntry?.isFallback ?? false);

        try {
          const barsResult = await fetchBarsWithFallback(sym, tf, barLimit(tf));
          useTerminalStore.getState().setBarsSource(barsResult.timeframeUsed, barsResult.isFallback);
          const sorted = mapAndDedup(barsResult.bars);
          useTerminalStore.getState().setBars(sorted);
          saveBarCache(sym, tf, sorted, barsResult.isFallback);
        } catch (e) { console.error("[useBarPolling:subscribe]", e); }
      }
    });

    const id = setInterval(poll, intervalMs);
    return () => {
      unsub();
      clearInterval(id);
    };
  }, [enabled, intervalMs]);
}
